import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ramsAPI } from '../../../api';
import Layout from '../../../components/Layout';
import { Tooltip, TooltipProvider } from '../../../components/ui/Tooltip';
import { Button } from '../../../components/ui/Button';
import { Input } from '../../../components/ui/Input';
import { Select } from '../../../components/ui/Select';
import { Badge } from '../../../components/ui/Badge';
import { Card, CardContent } from '../../../components/ui/Card';
import { FormLabel } from '../../../components/ui/FormLabel';
import {
  Table,
  TableHeader,
  TableBody,
  TableRow,
  TableHead,
  TableCell,
} from '../../../components/ui/Table';
import {
  Eye,
  Edit,
  Copy,
  GitBranch,
  FileDown,
  Plus,
} from 'lucide-react';
import { format } from 'date-fns';

const STATUS_OPTIONS = [
  { value: '', label: 'All statuses' },
  { value: 'draft', label: 'Draft' },
  { value: 'issued', label: 'Issued' },
  { value: 'approved', label: 'Approved' },
  { value: 'archived', label: 'Archived' },
];

function siteSnapshotSummary(siteSnapshot) {
  if (!siteSnapshot || typeof siteSnapshot !== 'object') return '—';
  const parts = [];
  if (siteSnapshot.client_name) parts.push(siteSnapshot.client_name);
  if (siteSnapshot.site_name) parts.push(siteSnapshot.site_name);
  const addr = siteSnapshot.address;
  if (addr) {
    const line = typeof addr === 'string' ? addr : addr?.line1 || addr?.line_1 || '';
    if (line) parts.push(line);
  }
  return parts.length ? parts.join(' – ') : '—';
}

function linkEntityLabel(linkEntityType) {
  if (!linkEntityType) return '—';
  const t = linkEntityType.toLowerCase();
  if (t === 'job') return 'Job';
  if (t === 'invoice') return 'Invoice';
  if (t === 'estimate') return 'Estimate';
  return linkEntityType;
}

function getStatusBadgeVariant(status) {
  const variants = {
    draft: 'neutral',
    issued: 'neutral',
    approved: 'success',
    archived: 'neutral',
  };
  return variants[status] || 'neutral';
}

export default function RamsList() {
  const navigate = useNavigate();
  const [rams, setRams] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 20,
    total: 0,
    totalPages: 0,
  });
  const [actionLoading, setActionLoading] = useState(null);

  useEffect(() => {
    fetchRams();
  }, [pagination.page, search, statusFilter]);

  const fetchRams = async () => {
    try {
      setLoading(true);
      setError('');
      const response = await ramsAPI.getAll({
        page: pagination.page,
        limit: pagination.limit,
        search: search || undefined,
        status: statusFilter || undefined,
      });
      setRams(response.data ?? []);
      setPagination((prev) => ({
        ...prev,
        ...(response.pagination ?? {}),
      }));
    } catch (err) {
      setError(err.message || 'Failed to load RAMS');
      setRams([]);
    } finally {
      setLoading(false);
    }
  };

  const handleClone = async (id) => {
    try {
      setActionLoading(id);
      const created = await ramsAPI.clone(id);
      if (created?.id) navigate(`/tenant/tools/rams/${created.id}/edit`);
      else fetchRams();
    } catch (err) {
      setError(err.message || 'Failed to clone RAMS');
    } finally {
      setActionLoading(null);
    }
  };

  const handleNewVersion = async (id) => {
    try {
      setActionLoading(id);
      const created = await ramsAPI.newVersion(id);
      if (created?.id) navigate(`/tenant/tools/rams/${created.id}/edit`);
      else fetchRams();
    } catch (err) {
      setError(err.message || 'Failed to create new version');
    } finally {
      setActionLoading(null);
    }
  };

  const handleDownloadPdf = async (id, title = 'RAMS') => {
    try {
      setActionLoading(id);
      const blob = await ramsAPI.getPDF(id);
      const url = URL.createObjectURL(new Blob([blob], { type: 'application/pdf' }));
      const link = document.createElement('a');
      link.href = url;
      link.download = `${title.replace(/[^\w\s-]/g, '') || 'RAMS'}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (err) {
      setError(err.message || 'Failed to download PDF');
    } finally {
      setActionLoading(null);
    }
  };

  return (
    <TooltipProvider>
      <Layout>
        <div className="bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700">
          <div className="max-w-7xl mx-auto px-6 py-6">
            <div className="md:flex md:items-center md:justify-between">
              <div className="flex-1 min-w-0">
                <h1 className="text-xl font-bold text-gray-900 dark:text-gray-100">
                  RAMS
                </h1>
                <p className="mt-1 text-sm text-gray-600 dark:text-gray-400">
                  Risk assessments and method statements
                </p>
              </div>
              <div className="mt-4 flex md:mt-0 md:ml-4 gap-3">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => navigate('/tenant/dashboard')}
                >
                  Back to Dashboard
                </Button>
                <Button
                  variant="primary"
                  size="sm"
                  onClick={() => navigate('/tenant/tools/rams/new')}
                >
                  <Plus className="h-4 w-4 mr-1.5" />
                  New RAMS
                </Button>
              </div>
            </div>
          </div>
        </div>

        <div className="max-w-7xl mx-auto px-6 py-8">
          <Card className="mb-6 bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700">
            <CardContent className="p-4">
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <div className="md:col-span-2">
                  <FormLabel className="text-gray-700 dark:text-gray-300">
                    Search
                  </FormLabel>
                  <Input
                    type="text"
                    value={search}
                    onChange={(e) => {
                      setSearch(e.target.value);
                      setPagination((prev) => ({ ...prev, page: 1 }));
                    }}
                    placeholder="Search by title or description..."
                    className="h-9 border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 placeholder-gray-500 dark:placeholder-gray-400"
                  />
                </div>
                <div>
                  <FormLabel className="text-gray-700 dark:text-gray-300">
                    Status
                  </FormLabel>
                  <Select
                    value={statusFilter}
                    onChange={(e) => {
                      setStatusFilter(e.target.value);
                      setPagination((prev) => ({ ...prev, page: 1 }));
                    }}
                    className="h-9 border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                  >
                    {STATUS_OPTIONS.map((opt) => (
                      <option key={opt.value} value={opt.value}>
                        {opt.label}
                      </option>
                    ))}
                  </Select>
                </div>
              </div>
            </CardContent>
          </Card>

          {error && (
            <div className="mb-4 p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-md">
              <p className="text-sm text-red-700 dark:text-red-300">{error}</p>
            </div>
          )}

          {loading ? (
            <div className="text-center py-12">
              <div className="inline-block h-8 w-8 animate-spin rounded-full border-2 border-gray-300 dark:border-gray-600 border-t-blue-600" />
              <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">
                Loading RAMS...
              </p>
            </div>
          ) : rams.length === 0 ? (
            <Card className="text-center py-12 bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700">
              <p className="text-sm text-gray-600 dark:text-gray-400">
                No RAMS found
              </p>
              <Button
                variant="primary"
                size="sm"
                onClick={() => navigate('/tenant/tools/rams/new')}
                className="mt-4"
              >
                Create RAMS
              </Button>
            </Card>
          ) : (
            <>
              <Card className="overflow-hidden bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700">
                <Table>
                  <TableHeader>
                    <TableRow isHeader>
                      <TableHead>Title</TableHead>
                      <TableHead>Version</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Site</TableHead>
                      <TableHead>Linked</TableHead>
                      <TableHead>Updated</TableHead>
                      <TableHead align="right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {rams.map((r) => (
                      <TableRow key={r.id}>
                        <TableCell>
                          <span className="font-medium text-gray-900 dark:text-gray-100">
                            {r.title || '—'}
                          </span>
                        </TableCell>
                        <TableCell className="text-gray-600 dark:text-gray-400">
                          v{r.version ?? 1}
                        </TableCell>
                        <TableCell>
                          <Badge variant={getStatusBadgeVariant(r.status)}>
                            {r.status || 'draft'}
                          </Badge>
                        </TableCell>
                        <TableCell className="max-w-[200px] truncate text-gray-600 dark:text-gray-400" title={siteSnapshotSummary(r.siteSnapshot)}>
                          {siteSnapshotSummary(r.siteSnapshot)}
                        </TableCell>
                        <TableCell className="text-gray-600 dark:text-gray-400">
                          {linkEntityLabel(r.linkEntityType)}
                        </TableCell>
                        <TableCell className="text-gray-600 dark:text-gray-400 tabular-nums">
                          {r.updatedAt
                            ? format(new Date(r.updatedAt), 'dd/MM/yyyy')
                            : '—'}
                        </TableCell>
                        <TableCell align="right">
                          <div className="flex items-center justify-end gap-0.5">
                            <Tooltip content="View">
                              <Button
                                variant="icon"
                                size="sm"
                                onClick={() => navigate(`/tenant/tools/rams/${r.id}`)}
                                className="text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-100 h-8 w-8"
                              >
                                <Eye className="h-4 w-4" />
                              </Button>
                            </Tooltip>
                            <Tooltip content="Edit">
                              <Button
                                variant="icon"
                                size="sm"
                                onClick={() => navigate(`/tenant/tools/rams/${r.id}/edit`)}
                                className="text-gray-600 dark:text-gray-400 hover:text-blue-600 dark:hover:text-blue-400 h-8 w-8"
                              >
                                <Edit className="h-4 w-4" />
                              </Button>
                            </Tooltip>
                            <Tooltip content="Clone">
                              <Button
                                variant="icon"
                                size="sm"
                                onClick={() => handleClone(r.id)}
                                disabled={actionLoading === r.id}
                                className="text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-100 h-8 w-8"
                              >
                                <Copy className="h-4 w-4" />
                              </Button>
                            </Tooltip>
                            <Tooltip content="New version">
                              <Button
                                variant="icon"
                                size="sm"
                                onClick={() => handleNewVersion(r.id)}
                                disabled={actionLoading === r.id}
                                className="text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-100 h-8 w-8"
                              >
                                <GitBranch className="h-4 w-4" />
                              </Button>
                            </Tooltip>
                            <Tooltip content="Download PDF">
                              <Button
                                variant="icon"
                                size="sm"
                                onClick={() => handleDownloadPdf(r.id, r.title)}
                                disabled={actionLoading === r.id}
                                className="text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-100 h-8 w-8"
                              >
                                <FileDown className="h-4 w-4" />
                              </Button>
                            </Tooltip>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </Card>

              {pagination.totalPages > 1 && (
                <Card className="mt-6 bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700">
                  <CardContent className="px-4 py-3 sm:px-6">
                    <div className="flex flex-wrap items-center justify-between gap-4">
                      <p className="text-xs text-gray-600 dark:text-gray-400">
                        Showing{' '}
                        <span className="font-medium">
                          {(pagination.page - 1) * pagination.limit + 1}
                        </span>{' '}
                        to{' '}
                        <span className="font-medium">
                          {Math.min(
                            pagination.page * pagination.limit,
                            pagination.total
                          )}
                        </span>{' '}
                        of <span className="font-medium">{pagination.total}</span>{' '}
                        results
                      </p>
                      <div className="flex gap-2">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() =>
                            setPagination((prev) => ({ ...prev, page: prev.page - 1 }))
                          }
                          disabled={pagination.page <= 1}
                        >
                          Previous
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() =>
                            setPagination((prev) => ({ ...prev, page: prev.page + 1 }))
                          }
                          disabled={pagination.page >= pagination.totalPages}
                        >
                          Next
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              )}
            </>
          )}
        </div>
      </Layout>
    </TooltipProvider>
  );
}
